/*!
 * Automatic Discount Script
 * Applies a visual discount based on theme settings.
 */

document.addEventListener('DOMContentLoaded', function () {
    // Prevent currency symbol from wrapping away from its value site-wide
    const noWrapStyle = document.createElement('style');
    noWrapStyle.textContent = '.price__regular, .price__sale { white-space: nowrap; }';
    document.head.appendChild(noWrapStyle);

    // Remove the space between currency symbol and amount ("$ 1,099.99" → "$1,099.99")
    function normalizeCurrencySpacing() {
        document.querySelectorAll('.price__regular, .price__sale').forEach(el => {
            el.childNodes.forEach(node => {
                if (node.nodeType === Node.TEXT_NODE) {
                    const fixed = node.textContent.replace(/([$€£¥])\s+(\d)/g, '$1$2');
                    if (fixed !== node.textContent) node.textContent = fixed;
                }
            });
        });
    }
    normalizeCurrencySpacing();
    let _normT;
    new MutationObserver(() => { clearTimeout(_normT); _normT = setTimeout(normalizeCurrencySpacing, 60); })
        .observe(document.body, { subtree: true, childList: true });

    const settings = window.AutomaticDiscountSettings || { enabled: false };
    if (!settings.enabled) return;

    // Never alter prices on the cart page or checkout
    if (document.body.classList.contains('template-cart')) return;

    const percentage = settings.percentage || 25;
    const multiplier = 1 - (percentage / 100);
    const discountText = settings.text || `${percentage}% off with code BlackFriday`;

    // Parse exclusions
    const excludedIds = (settings.exclusions || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

    // Selectors
    // .price: standard product price
    // product-buy-price .price: add to cart button price (custom element in this theme)
    const PRICE_SELECTORS = '.price, product-buy-price .price';

    const parseMoneyToCents = (txt) => {
        if (!txt) return null;
        const num = txt.replace(/[^0-9.-]/g, '');
        if (!num) return null;
        return Math.round(parseFloat(num) * 100);
    };

    const formatCents = (cents) => {
        try {
            if (window.Shopify && Shopify.formatMoney) {
                return Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}');
            }
        } catch (e) { }
        return '$' + (cents / 100).toFixed(2);
    };

    const debounce = (fn, wait = 60) => {
        let t;
        return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
    };

    const flipMode = !!settings.flipExclusions;

    // Normal mode: returns true if id is in the list (excluded).
    // Flip mode: returns true if id is NOT in the list (only listed IDs get the discount).
    const checkId = (id) => {
        const inList = excludedIds.includes(String(id));
        return flipMode ? !inList : inList;
    };

    function isExcluded(priceEl) {
        // 1. Check global currentProductId (PDP, non-card price elements only)
        if (window.currentProductId && !priceEl.closest('.product-card')) {
            return checkId(window.currentProductId);
        }

        // 2. Check for data-product-id on the card or container
        const card = priceEl.closest('.product-card, .product-form, [data-product-id]');
        if (card) {
            const id = card.dataset.productId || card.getAttribute('data-product-id');
            if (id) return checkId(id);
        }

        // No product ID found — in flip mode treat as excluded (not in allow-list)
        return flipMode;
    }

    function cleanupInjected(priceEl) {
        priceEl.querySelectorAll('.gpt-was-price, .gpt-sale-label').forEach(n => n.remove());
        const cardDetails = priceEl.closest('.product-card__details');
        if (cardDetails && cardDetails.parentElement) {
            cardDetails.parentElement.querySelectorAll(':scope > .gpt-sale-label').forEach(n => n.remove());
        }
        delete priceEl.dataset.lastDiscountedCents;
        priceEl.classList.remove('gpt-discount-applied');

        // Reset visibility of original elements if needed
        // (Not strictly necessary if we only appended things, but good for "was" price)
        const saleNode = priceEl.querySelector('.price__sale');
        if (saleNode && saleNode.classList.contains('gpt-was-price')) {
            saleNode.remove();
            priceEl.classList.remove('price--on-sale');
        }
    }

    function applyDiscount(priceEl) {
        if (priceEl.closest('template')) return;

        // Never alter prices inside the cart drawer or any cart container
        if (priceEl.closest('cart-drawer, #CartDrawer, .cart-drawer, cart-items, .cart-items, [data-section-type="cart-drawer"]')) return;

        // Product bundle add-ons on the PDP (the "add-on" items inside the accordion)
        // Each item must be checked against its own product ID — not the main product's.
        if (priceEl.closest('[is="product-bundle-details"]')) {
            const bundleItem = priceEl.closest('.product-bundle__info');
            if (!bundleItem || !bundleItem.dataset.productId || checkId(bundleItem.dataset.productId)) {
                // No ID available, or this add-on's own product is excluded — skip.
                cleanupInjected(priceEl);
                return;
            }
            // This add-on's product qualifies for the discount — fall through without
            // calling isExcluded() below (which would wrongly use the main product's ID).
        } else {
            // Regular price element — use the normal exclusion check.
            if (isExcluded(priceEl)) {
                cleanupInjected(priceEl);
                return;
            }
        }

        const soldOutEl = priceEl.querySelector('.price--sold-out');
        if (soldOutEl) { cleanupInjected(priceEl); return; }

        // Identify if this is the Add to Cart button price
        const isAddToCart = priceEl.closest('product-buy-price') || priceEl.parentElement.id.includes('BuyButtonPrice');

        // Get the text content to parse
        // For Add to Cart, the structure might be different.
        // <product-buy-price class="price"> $524.99 </product-buy-price>
        // It might not have .price__regular inside.

        let regularPriceNode = priceEl.querySelector('.price__regular');

        // If it's the simple structure (like add to cart button often is in this theme)
        if (!regularPriceNode && isAddToCart) {
            // The element itself might contain the text
            regularPriceNode = priceEl;
        } else if (!regularPriceNode && priceEl.classList.contains('price')) {
            // Maybe it's just a simple .price element without inner structure?
            // But theme usually has structure.
            // If on sale, check .price__sale?
            if (priceEl.classList.contains('price--on-sale')) {
                // In this theme, .price__regular is ALWAYS present and holds the selling price.
                regularPriceNode = priceEl.querySelector('.price__regular');
            }
        }

        if (!regularPriceNode) return;

        const raw = regularPriceNode.textContent.trim();
        const parsed = parseMoneyToCents(raw);

        if (parsed === 0 || parsed == null) {
            cleanupInjected(priceEl);
            return;
        }

        // Store original value to avoid compounding discounts on re-runs
        let originalCents = parsed;
        if (priceEl.dataset.originalCents) {
            originalCents = parseInt(priceEl.dataset.originalCents, 10);
        } else {
            priceEl.dataset.originalCents = originalCents;
        }

        const discountedCents = Math.ceil(originalCents * multiplier);

        // Update the displayed price
        // Use innerHTML to replace text but keep any spans if they exist (though usually money is text)
        // We use formatMoney to get currency symbol
        regularPriceNode.innerHTML = formatCents(discountedCents);
        // Force normal color (override theme's sale color)
        regularPriceNode.style.color = 'var(--color-price, #171717)';

        // Ensure price items stack vertically at all sizes (sale price then was-price below)
        if (priceEl.classList.contains('lg:flex-col')) {
            priceEl.classList.remove('lg:flex-col');
            priceEl.classList.remove('xl:items-end');
            priceEl.classList.add('flex-col');
        }

        // Handle "Was" price
        // Only for main prices, not necessarily for Add to Cart button (unless space permits)
        // User said "The add to cart button is showing the old price." -> implies they want it updated.
        // Usually Add to Cart button just shows the current price to pay.

        if (!isAddToCart) {
            let saleNode = priceEl.querySelector('.price__sale');

            if (!saleNode) {
                saleNode = document.createElement('span');
                saleNode.className = 'price__sale inline-flex items-center h-auto relative gpt-was-price';
                // Force visibility and styling
                saleNode.style.cssText = 'display: inline-flex !important; text-decoration: line-through !important; color: #1778B9;';

                priceEl.appendChild(saleNode);
                priceEl.classList.add('price--on-sale');

                saleNode.textContent = formatCents(originalCents);
            } else {
                // If it exists, ensure it's visible
                saleNode.style.display = 'inline-flex';
                // If it's our injected one, make sure it has the right value
                if (saleNode.classList.contains('gpt-was-price')) {
                    saleNode.textContent = formatCents(originalCents);
                }
                // If it's theme's own (MSRP), we leave it alone.
            }

            // Add label — on product cards insert it after the details row (between
            // title+price and swatches) so it spans full width in the flex-col flow.
            // On PDP (no .product-card__details ancestor) keep it inside the price element.
            const cardDetails = priceEl.closest('.product-card__details');
            if (cardDetails) {
                let lbl = cardDetails.parentElement && cardDetails.parentElement.querySelector(':scope > .gpt-sale-label');
                if (!lbl) {
                    lbl = document.createElement('span');
                    lbl.className = 'gpt-sale-label';
                    lbl.style.cssText = 'display:block; font-size:smaller; font-style:italic; color:#ef4444; margin-top:2px;';
                    cardDetails.insertAdjacentElement('afterend', lbl);
                }
                lbl.textContent = discountText;
            } else {
                let lbl = priceEl.querySelector('.gpt-sale-label');
                if (!lbl) {
                    lbl = document.createElement('span');
                    lbl.className = 'gpt-sale-label';
                    lbl.style.cssText = 'display:block; font-size:smaller; font-style:italic; margin-top:2px; color:#ef4444;';
                    priceEl.appendChild(lbl);
                }
                lbl.textContent = discountText;
            }
        }

        priceEl.dataset.lastDiscountedCents = String(discountedCents);
    }

    function updateAllPrices() { document.querySelectorAll(PRICE_SELECTORS).forEach(applyDiscount); }
    const debouncedUpdateAllPrices = debounce(updateAllPrices, 50);

    // Initial + reactive hooks
    updateAllPrices();

    document.addEventListener('variant:changed', debouncedUpdateAllPrices, true);
    document.addEventListener('product:variant-change', debouncedUpdateAllPrices, true);
    document.addEventListener('product:change', debouncedUpdateAllPrices, true);
    document.addEventListener('change', function (e) {
        const t = e.target;
        if (t && (t.name === 'id' || t.matches('[name="id"], .single-option-selector, [data-product-variant-selector]'))) {
            debouncedUpdateAllPrices();
        }
    }, true);

    const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
            if (m.type === 'childList' && m.addedNodes.length > 0) {
                debouncedUpdateAllPrices();
                break;
            }
        }
    });

    observer.observe(document.body, { subtree: true, childList: true });
});
